import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, ShoppingBasket, Heart, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { imgUrl } from "../lib/api";
import { useLang } from "../i18n";
import { useCart } from "../context/CartContext";
import ProductCard from "../components/ProductCard";

export default function ProductDetail() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { addItem, setDrawerOpen, wishlist, toggleWishlist } = useCart();
  const [product, setProduct] = useState(null);
  const [all, setAll] = useState([]);
  const [sizeIdx, setSizeIdx] = useState(0);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setProduct(null);
    setSizeIdx(0);
    setQty(1);
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => {});
    api.get("/products").then((r) => setAll(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, [id]);

  const related = useMemo(
    () => all.filter((p) => p.category === product?.category && p.id !== product?.id).slice(0, 4),
    [all, product]
  );

  if (!product) return <p className="py-32 text-center font-mono text-sm text-cocoa/50" data-testid="pdp-loading">{t("common.loading")}</p>;

  const size = product.sizes[sizeIdx];
  const wished = wishlist.includes(product.id);
  const tasting = product.tasting || {};

  const handleAdd = () => {
    addItem(product, size, qty);
    toast.success(t("common.added"));
    setDrawerOpen(true);
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14" data-testid="product-detail-page">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="relative">
          <div className="absolute -top-4 -left-4 w-full h-full border border-honey/40 rounded-sm" />
          <div className="relative overflow-hidden rounded-sm border border-[#E5D9C8] bg-white">
            <img src={imgUrl(product.image)} alt={product.name_it} className="w-full aspect-[4/5] object-cover" data-testid="pdp-image" />
            <div className="absolute inset-0 bg-gradient-to-t from-bark/40 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 flex items-center gap-1.5 bg-bark/70 backdrop-blur text-parchment text-[11px] font-mono px-3 py-1.5" data-testid="pdp-gps">
              <MapPin size={12} className="text-gold" /> {t("pdp.apiary")}
            </div>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber-800">{t(`cat.${product.category}`)}</p>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl font-bold tracking-tight leading-tight" data-testid="pdp-name">
            {lang === "it" ? product.name_it : product.name_en}
          </h1>
          <p className="mt-5 text-cocoa/70 leading-relaxed" data-testid="pdp-desc">{lang === "it" ? product.desc_it : product.desc_en}</p>

          <div className="mt-7 grid grid-cols-3 gap-4 border-y border-[#E5D9C8] py-5 text-sm">
            <div><p className="font-mono text-[10px] uppercase tracking-widest text-cocoa/50">{t("pdp.origin")}</p><p className="mt-1 font-medium italic font-serif" data-testid="pdp-botanical">{product.botanical}</p></div>
            <div><p className="font-mono text-[10px] uppercase tracking-widest text-cocoa/50">{t("pdp.harvest")}</p><p className="mt-1 font-medium" data-testid="pdp-harvest">{product.harvest}</p></div>
            <div><p className="font-mono text-[10px] uppercase tracking-widest text-cocoa/50">{t("pdp.batch")}</p><p className="mt-1 font-mono text-honey font-semibold" data-testid="pdp-batch">#{product.batch}</p></div>
          </div>

          <div className="mt-7">
            <p className="font-mono text-[10px] uppercase tracking-widest text-cocoa/50 mb-3">{t("pdp.size")}</p>
            <div className="flex flex-wrap gap-2" data-testid="size-selector">
              {product.sizes.map((s, i) => (
                <button key={s.label} onClick={() => setSizeIdx(i)} data-testid={`size-option-${i}`}
                  className={`px-5 py-2.5 border text-sm font-medium transition-colors ${i === sizeIdx ? "bg-bark text-parchment border-bark" : "border-[#E5D9C8] hover:border-honey"}`}>
                  {s.label} · €{s.price.toFixed(2)}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-7 flex items-center gap-5">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-cocoa/50 mb-2">{t("pdp.qty")}</p>
              <div className="flex items-center gap-3 border border-[#E5D9C8] px-3 py-2 w-fit">
                <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-minus" className="hover:text-honey transition-colors"><Minus size={15} /></button>
                <span className="font-mono w-6 text-center" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty(qty + 1)} data-testid="qty-plus" className="hover:text-honey transition-colors"><Plus size={15} /></button>
              </div>
            </div>
            <div className="flex-1" />
            <p className="font-mono text-3xl font-semibold text-honey" data-testid="pdp-price">€{(size.price * qty).toFixed(2)}</p>
          </div>

          <div className="mt-7 flex gap-3">
            <button onClick={handleAdd} data-testid="pdp-add-to-cart"
              className="flex-1 flex items-center justify-center gap-2 bg-bark text-parchment py-4 font-medium hover:bg-honey transition-colors duration-300">
              <ShoppingBasket size={17} /> {t("pdp.addcart")}
            </button>
            <button onClick={() => { toggleWishlist(product.id); toast.success(wished ? t("common.wishlist.remove") : t("common.wishlist.add")); }}
              data-testid="pdp-wishlist-btn"
              className={`px-4 border transition-colors ${wished ? "bg-terra text-parchment border-terra" : "border-[#E5D9C8] hover:border-terra hover:text-terra"}`}>
              <Heart size={18} fill={wished ? "currentColor" : "none"} />
            </button>
          </div>

          <div className="mt-10">
            <p className="font-mono text-[10px] uppercase tracking-widest text-cocoa/50 mb-4">{t("pdp.tasting")}</p>
            <div className="space-y-3" data-testid="tasting-notes">
              {["dolcezza", "aromaticita", "cristallizzazione", "persistenza"].map((k) => (
                <div key={k} className="flex items-center gap-4">
                  <span className="text-sm w-32 text-cocoa/70">{t(`taste.${k}`)}</span>
                  <div className="flex-1 h-1.5 bg-[#E5D9C8] rounded-full overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${((tasting[k] || 0) / 5) * 100}%` }} transition={{ duration: 0.9, delay: 0.3 }}
                      className="h-full bg-honey rounded-full" data-testid={`taste-bar-${k}`} />
                  </div>
                  <span className="font-mono text-xs text-cocoa/50 w-6 text-right">{tasting[k] || 0}/5</span>
                </div>
              ))}
            </div>
          </div>

          {(product.pairings_it || product.pairings_en) && (
            <div className="mt-8 bg-cream/70 border border-[#E5D9C8] px-5 py-4" data-testid="pdp-pairings">
              <p className="font-mono text-[10px] uppercase tracking-widest text-amber-800 mb-1.5">{t("pdp.pairings")}</p>
              <p className="font-serif italic text-lg">{lang === "it" ? product.pairings_it : product.pairings_en}</p>
            </div>
          )}
        </motion.div>
      </div>

      {related.length > 0 && (
        <div className="mt-24">
          <h2 className="font-serif text-3xl font-semibold mb-10" data-testid="related-title">{t("pdp.related")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </div>
      )}
    </div>
  );
}
